import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp,
  CheckCircle,
  Clock,
  AlertTriangle,
  Users,
  Activity,
  Calendar,
  Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import taskService from '@/services/taskService';

const TaskDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const data = await taskService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const PRIORITY_COLORS = {
    urgente: '#ef4444',
    alta: '#f97316',
    media: '#f59e0b',
    baixa: '#10b981'
  };

  const priorityData = stats?.cards_by_priority ? Object.keys(stats.cards_by_priority).map(priority => ({
    name: taskService.translatePriority(priority),
    value: stats.cards_by_priority[priority],
    color: PRIORITY_COLORS[priority]
  })) : [];

  const statusData = stats?.boards_stats?.map(board => ({
    name: board.board_title,
    concluidos: board.completed_cards,
    total: board.total_cards,
    taxa: board.completion_rate
  })) || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard de Tarefas</h1>
          <p className="text-gray-600 mt-1">Visão geral de produtividade e métricas</p>
        </div>
        <Button onClick={() => navigate('/portal/tasks')}>
          Ver Todos os Boards
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Cards</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">{stats?.total_cards || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cards Concluídos</p>
                <h3 className="text-3xl font-bold text-green-600 mt-2">{stats?.completed_cards || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Em Progresso</p>
                <h3 className="text-3xl font-bold text-orange-600 mt-2">{stats?.in_progress_cards || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cards Atrasados</p>
                <h3 className="text-3xl font-bold text-red-600 mt-2">{stats?.overdue_cards || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart - Cards por Prioridade */}
        <Card>
          <CardHeader>
            <CardTitle>Cards por Prioridade</CardTitle>
            <CardDescription>Distribuição de cards por nível de prioridade</CardDescription>
          </CardHeader>
          <CardContent>
            {priorityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={priorityData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                Nenhum card criado ainda
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bar Chart - Progresso por Board */}
        <Card>
          <CardHeader>
            <CardTitle>Progresso por Board</CardTitle>
            <CardDescription>Taxa de conclusão de cards por board</CardDescription>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="concluidos" fill="#10b981" name="Concluídos" />
                  <Bar dataKey="total" fill="#3b82f6" name="Total" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                Nenhum board criado ainda
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Upcoming Deadlines */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Atividade Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_activities?.length > 0 ? (
                stats.recent_activities.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 pb-3 border-b last:border-b-0">
                    <div className="w-2 h-2 rounded-full bg-blue-600 mt-2"></div>
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-medium">{activity.user_name}</span>
                        {' '}
                        <span className="text-gray-600">{activity.description}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">Nenhuma atividade recente</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Próximos Vencimentos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.upcoming_deadlines?.length > 0 ? (
                stats.upcoming_deadlines.map((card, index) => {
                  const daysUntilDue = Math.ceil(
                    (new Date(card.due_date) - new Date()) / (1000 * 60 * 60 * 24)
                  );
                  const isOverdue = daysUntilDue < 0;
                  const isUrgent = daysUntilDue <= 2 && daysUntilDue >= 0;

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                      onClick={() => navigate(`/portal/tasks/board/${card.board_id}`)}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm">{card.title}</p>
                        <p className="text-xs text-gray-600">{card.board_title}</p>
                      </div>
                      <div>
                        {isOverdue ? (
                          <Badge variant="destructive" className="text-xs">
                            {Math.abs(daysUntilDue)}d atrasado
                          </Badge>
                        ) : isUrgent ? (
                          <Badge className="text-xs bg-orange-500">
                            {daysUntilDue}d restante
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            {daysUntilDue}d restante
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-gray-500 py-8">Nenhum vencimento próximo</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Boards Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Visão Geral dos Boards</CardTitle>
          <CardDescription>Estatísticas detalhadas de cada board</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats?.boards_stats?.length > 0 ? (
              stats.boards_stats.map((board) => (
                <div
                  key={board.board_id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/portal/tasks/board/${board.board_id}`)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{board.icon || '📋'}</div>
                      <div>
                        <h4 className="font-semibold">{board.board_title}</h4>
                        <p className="text-sm text-gray-600">
                          {board.total_cards} cards • {board.column_count} colunas
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{board.completion_rate}%</p>
                      <p className="text-xs text-gray-600">Taxa de conclusão</p>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${board.completion_rate}%` }}
                    ></div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Concluídos</p>
                      <p className="text-lg font-semibold text-green-600">{board.completed_cards}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Em Progresso</p>
                      <p className="text-lg font-semibold text-orange-600">{board.in_progress_cards}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Pendentes</p>
                      <p className="text-lg font-semibold text-blue-600">{board.pending_cards}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Atrasados</p>
                      <p className="text-lg font-semibold text-red-600">{board.overdue_cards}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Target className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhum board criado
                </h3>
                <p className="text-gray-600 mb-4">
                  Crie seu primeiro board para começar a organizar tarefas
                </p>
                <Button onClick={() => navigate('/portal/tasks')}>
                  Criar Primeiro Board
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaskDashboard;
